import React from 'react';
import * as Sentry from '@sentry/react';
import { Icon, IconName } from '@hills-design-system/components';
import Modal from './Modal';
import { ModalSize } from './Modal.types';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import { gigyaLogoutAndLogin, gigyaLogoutAndRedirect, gigyaLogoutAndRegister } from '../../services/gigya';
import { Tooltip } from '../Tooltip';
import { getFoodShelterLoveRedirectUrl } from '../../utils/redirectUrl';

const baseURL = import.meta.env.VITE_HILLSVET_BASE_URL;

interface AccessRestrictedModalProps {
    isRetailAccount: boolean;
}
const AccessRestrictedModal: React.FC<AccessRestrictedModalProps> = ({isRetailAccount}) => {
    const { t } = useTranslation();

    const handleLogin = async () => {
        const redirectUrl = getFoodShelterLoveRedirectUrl();
        try {
            await gigyaLogoutAndLogin(redirectUrl);
        } catch (error) {
            console.error('[NavigationMenu] Logout error:', error);
            window.location.href = redirectUrl;
        }
    };

    const handleRegister = async () => {
        const redirectUrl = `${baseURL?.replace(/\/$/, '')}/register`;
        try {
            await gigyaLogoutAndRegister(redirectUrl);
        } catch (error) {
            Sentry.captureException(error);
            console.error('[AccessRestrictedModal] Register error');
            window.location.href = redirectUrl;
        }
    };

    const handleRedirect = async (path: string) => {
        const redirectUrl = `${getFoodShelterLoveRedirectUrl().replace(/\/$/, '')}/${path}`;
        try {
            await gigyaLogoutAndRedirect(redirectUrl);
        } catch (error) {
            Sentry.captureException(error);
            console.error('[AccessRestrictedModal] Logout error');
            window.location.href = redirectUrl;
        } finally {
            // Clear cached auth token so hasCachedAuth() returns false when the
            // user returns to FSL after applying.
            localStorage.removeItem('hills_fsl_auth');
        }
    };

  

    return (
        <Modal
            title={t(TranslationKeyEnum.AccessRestrictedModalTitle)}
            isOpen={true}
            onClose={() => {}} // Prevent closing this modal
            size={ModalSize.Medium}
            showCloseButton={false}
            className="max-w-2xl text-center"
        >
            <div className="space-y-6">
                <div className="space-y-4">
                    <div className="flex items-center bg-red-200">
                        <div className="relative flex items-center justify-center p-4">
                            <div className="w-12 h-12 ml-2 bg-gray-50 rounded-full flex items-center justify-center">
                                <div className="absolute inset-0 m-auto size-12 rounded-full">
                                    {/* icons circle background */}
                                </div>
                                <Icon icon={IconName.WarningOutline} size={32} className='text-red-600'/>
                            </div>
                        </div>
                        {isRetailAccount ? (
                            <p className="text-gray-700 text-xs leading-relaxed mx-2 text-start">
                                {t(TranslationKeyEnum.AccessRestrictedModalDescriptionRetail1)}{' '}
                                <strong>{t(TranslationKeyEnum.AccessRestrictedModalHillsRetail)}</strong>{' '}
                                {t(TranslationKeyEnum.AccessRestrictedModalDescriptionRetail2)}{' '}
                                <strong>{t(TranslationKeyEnum.AccessRestrictedModalPortalName)}</strong>
                                {t(TranslationKeyEnum.AccessRestrictedModalDescriptionRetail3)}
                            </p>
                        ) : (
                            <p className="text-gray-700 text-xs leading-relaxed mx-4 text-start">
                                {t(TranslationKeyEnum.AccessRestrictedModalDescriptionOther)}{' '}
                                <strong>{t(TranslationKeyEnum.AccessRestrictedModalPortalName)}</strong>{'.'}
                            </p>
                        )}
                    </div>
                    <div className="space-y-3 text-start">
                        <div className='bg-blue-0 rounded-lg px-8 py-6'>
                            <p className="text-gray-700 text-xs leading-relaxed pb-4">
                                {t(TranslationKeyEnum.AccessRestrictedModalListHeader)}
                            </p>
                            <ul className="text-gray-700 text-base leading-6 text-start rounded-lg">
                                <li className="flex gap-2 items-center font-bold">
                                    <Icon icon={IconName.Circle} size={8} />{' '}
                                    {t(TranslationKeyEnum.AccessRestrictedModalBullet1)}
                                </li>
                                <li className="flex gap-4 items-center text-xs leading-5">
                                    <Icon icon={IconName.Circle} size={0} />{' '}
                                    <p>
                                        {t(TranslationKeyEnum.AccessRestrictedModalBullet1Sub1)}{' '}
                                        <button className='text-blue-600 text-xs border-none' onClick={handleLogin}>
                                            <strong>{t(TranslationKeyEnum.AccessRestrictedModalBullet1SubSignIn)}</strong>
                                        </button>{' '}
                                        {t(TranslationKeyEnum.AccessRestrictedModalBullet1Sub2)}
                                    </p>
                                </li>
                                <li className="flex gap-2 items-center font-bold mt-4">
                                    <Icon icon={IconName.Circle} size={8} />{' '}
                                    {t(TranslationKeyEnum.AccessRestrictedModalBullet2)}
                                </li>
                                <li className="flex gap-4 items-center text-xs leading-5">
                                    <Icon icon={IconName.Circle} size={0} />{' '}
                                    <p>
                                        {t(TranslationKeyEnum.AccessRestrictedModalBullet2Sub2Beginning)}{' '}
                                        <button className='text-blue-600 text-xs border-none' onClick={handleRegister}>
                                            <strong>{t(TranslationKeyEnum.AccessRestrictedModalBullet2Sub2Register)}</strong>
                                        </button>{' '}
                                        {t(TranslationKeyEnum.AccessRestrictedModalBullet2Sub2Middle)}{' '}
                                        <strong>{t(TranslationKeyEnum.AccessRestrictedModalBullet2Sub2AccountNumber)}</strong>{' '}
                                        {t(TranslationKeyEnum.AccessRestrictedModalBullet2Sub2End)}{' '}
                                        <strong>{t(TranslationKeyEnum.AccessRestrictedModalBullet2Sub2BillingZip)}</strong>
                                        <span className='relative inline-block ml-2'>
                                            <Tooltip
                                                align="right"
                                                content={
                                                    <div className="w-[360px]  p-4">
                                                    <div className="text-sm font-extralight text-white leading-[1.375]">
                                                        {t(TranslationKeyEnum.AccessRestrictedModalToolTip)}
                                                    </div>
                                                    </div>
                                                }
                                            />
                                        </span>
                                    </p>
                                </li>
                                <li className="flex gap-2 items-center font-bold mt-4">
                                    <Icon icon={IconName.Circle} size={8} />{' '}
                                    {t(TranslationKeyEnum.AccessRestrictedModalRetailBullet3)}
                                </li>
                                <li className="flex gap-4 items-center text-xs leading-5">
                                    <Icon icon={IconName.Circle} size={0} />{' '}
                                    <p>
                                        {t(TranslationKeyEnum.AccessRestrictedModalRetailBullet3Sub3Beginning)}{' '}
                                        <button className='text-blue-600 text-xs border-none' onClick={() => handleRedirect('apply')}>
                                            <strong>{t(TranslationKeyEnum.AccessRestrictedModalRetailBullet3Sub3Apply)}</strong>
                                        </button>{' '}
                                        {t(TranslationKeyEnum.AccessRestrictedModalRetailBullet3Sub3End)}
                                    </p>
                                </li>
                            </ul>
                        </div>
                        <div className='border rounded-lg grid grid-cols-2 p-4 gap-8'>
                            <div className='content-center'>
                                <p className='font-bold text-sm'>
                                    {t(TranslationKeyEnum.AccessRestrictedModalSupport)}
                                </p>
                            </div>
                            <div>
                                <p className='flex items-center gap-2'>
                                    <Icon icon={IconName.Smartphone} size={12} className='text-blue-600'/>
                                    <a className="text-blue-600 text-sm" href="tel:1-866-283-1217">
                                        {t(TranslationKeyEnum.HillsPhone)}
                                    </a>
                                </p>
                                <p className='text-gray-500 text-xs mb-2'>
                                    {t(TranslationKeyEnum.HillsHours)}
                                </p>
                                <p className='flex items-center gap-2'>
                                    <Icon icon={IconName.Mail} size={12} className='text-blue-600'/>
                                    <a className="text-blue-600 text-sm" href="mailto:shelters@hillspet.com">
                                        {t(TranslationKeyEnum.EmailUs)}
                                    </a>
                                </p>
                                <p className='text-gray-500 text-xs mb-2'>
                                    {t(TranslationKeyEnum.HillsEmail)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AccessRestrictedModal;
